import {
  Component, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import {
  PoAccordionItemComponent, PoButtonGroupItem, PoModalAction, PoModalComponent
} from '@po-ui/ng-components';

import { resourceTypeLabel } from '../../../core/utils/resource-type-label.util';
import { normalizeSeverity, severityColor } from '../../../review/utils/severity.util';
import {
  ReviewStatsFileFinding, ReviewStatsFileReview, ReviewStatsPeriodFilters
} from '../../interfaces/review-stats.interface';
import { ReviewStatsService } from '../../review-stats.service';
import { reviewCategoryLabel } from '../../utils/review-category-label.util';

type TagType = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

/** Recorte do filtro: uma severidade de ação obrigatória, as sugestões, ou tudo. */
type FindingFilter = 'all' | 'ALTA' | 'MEDIA' | 'BAIXA' | 'suggestion';

interface FindingCard {
  /** Severidade (ação obrigatória) ou categoria (sugestão). */
  tag: string;
  tagType: TagType;
  kind: string;
  line: string;
  description: string;
  action: string;
  code: string;
  filterKey: FindingFilter;
}

interface ReviewBlock {
  /** Cabeçalho do item: nome do review, tipo e data. */
  label: string;
  /** Tag no cabeçalho — o que o review tem de mais grave, visível com o item fechado. */
  tag: string;
  tagType: TagType;
  verdict: string;
  verdictType: TagType;
  resourceType: string;
  resourceId: string;
  findings: FindingCard[];
  /** Mensagem no lugar dos achados: review limpo ou resultado indisponível. */
  note: string;
}

interface SummaryTag {
  label: string;
  type: TagType;
  filterKey: FindingFilter;
  count: number;
}

const SEVERITY_LABELS: Record<string, string> = { ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa' };
const SEVERITY_ORDER: Record<string, number> = { ALTA: 0, MEDIA: 1, BAIXA: 2 };
const VERDICTS: Record<string, { label: string; type: TagType }> = {
  REJEITADO: { label: 'Rejeitado', type: 'danger' },
  AJUSTES_NECESSARIOS: { label: 'Ajustes necessários', type: 'warning' },
  APROVADO: { label: 'Aprovado', type: 'success' },
  INSUFICIENTE: { label: 'Resposta inválida', type: 'neutral' }
};
const UNAVAILABLE_NOTES: Record<string, string> = {
  missing: 'O resultado deste review não está mais disponível no armazenamento.',
  unreadable: 'O resultado deste review está num formato que não pôde ser lido.',
  overwritten: 'O resultado foi substituído pelo de outro changeset da mesma issue — abra o review para vê-lo.'
};

/**
 * Os achados que os reviews recentes apontaram num fonte do ranking: o caminho do número
 * até a correção.
 *
 * Um item de accordion por review, o mais recente aberto. Achado é cartão, e não linha de
 * tabela: descrição e ação são texto longo, com código, e numa tabela ficavam cortadas
 * atrás de rolagem horizontal — justamente a parte que diz o que corrigir.
 */
@Component({
  selector: 'app-review-stats-file-findings',
  templateUrl: './review-stats-file-findings.component.html',
  styleUrls: ['./review-stats-file-findings.component.css']
})
export class ReviewStatsFileFindingsComponent implements OnDestroy {
  @Input() filters: ReviewStatsPeriodFilters = {};
  /** Avisa antes de navegar, para quem abriu este modal por cima de outro fechá-lo. */
  @Output() reviewOpened = new EventEmitter<void>();
  @ViewChild(PoModalComponent, { static: true }) modal?: PoModalComponent;
  @ViewChildren(PoAccordionItemComponent) accordionItems?: QueryList<PoAccordionItemComponent>;

  path = '';
  /** O caminho partido para o destaque: a pasta discreta, o nome do arquivo em evidência. */
  folder = '';
  fileName = '';
  copied = false;
  filter: FindingFilter = 'all';
  filterButtons: PoButtonGroupItem[] = [];
  blocks: ReviewBlock[] = [];
  summary: SummaryTag[] = [];
  total = 0;
  loading = false;
  loadError = false;

  readonly closeAction: PoModalAction = { label: 'Fechar', action: () => this.modal?.close() };

  private requestSequence = 0;
  private copiedTimer: number | null = null;

  constructor(private statsService: ReviewStatsService, private router: Router) {}

  get title(): string {
    return `Achados em ${this.fileName || this.path}`;
  }

  ngOnDestroy(): void {
    if (this.copiedTimer != null) { window.clearTimeout(this.copiedTimer); }
  }

  /** Achados do review que passam no filtro escolhido. */
  visibleFindings(block: ReviewBlock): FindingCard[] {
    return this.filter === 'all' ? block.findings : block.findings.filter(f => f.filterKey === this.filter);
  }

  /** Por que um review com achados não mostra nenhum: o filtro os escondeu. */
  filteredOut(block: ReviewBlock): boolean {
    return !block.note && block.findings.length > 0 && this.visibleFindings(block).length === 0;
  }

  /** O caminho vai direto para o TFVC ou para o editor. */
  copyPath(): void {
    if (!this.path || !navigator.clipboard) { return; }
    navigator.clipboard.writeText(this.path).then(() => {
      this.copied = true;
      if (this.copiedTimer != null) { window.clearTimeout(this.copiedTimer); }
      this.copiedTimer = window.setTimeout(() => {
        this.copied = false;
        this.copiedTimer = null;
      }, 2000);
    }).catch(() => undefined);
  }

  /** Diz quando a lista é um recorte dos reviews do fonte. */
  get scopeLabel(): string {
    if (!this.total) { return ''; }
    return this.total > this.blocks.length
      ? `Os ${this.blocks.length} resultados mais recentes de ${this.total.toLocaleString('pt-BR')} neste recorte.`
      : `${this.total.toLocaleString('pt-BR')} ${this.total === 1 ? 'resultado' : 'resultados'} neste recorte.`;
  }

  open(path: string, language?: string): void {
    this.path = path;
    const cut = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
    this.folder = cut >= 0 ? path.slice(0, cut + 1) : '';
    this.fileName = cut >= 0 ? path.slice(cut + 1) : path;
    this.copied = false;
    this.filter = 'all';
    this.filterButtons = [];
    this.blocks = [];
    this.summary = [];
    this.total = 0;
    this.loading = true;
    this.loadError = false;
    this.modal?.open();
    const request = ++this.requestSequence;
    this.statsService.getFileFindings({ ...this.filters, path, language }).subscribe({
      next: result => {
        if (request !== this.requestSequence) { return; }
        this.blocks = result.reviews.map(review => this.toBlock(review));
        this.summary = this.summarize(result.reviews);
        this.filterButtons = this.buildFilterButtons();
        this.total = result.total;
        this.loading = false;
        this.expandFirst();
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  openReview(block: ReviewBlock): void {
    this.modal?.close();
    this.reviewOpened.emit();
    this.router.navigate(['/menu/smart-review/reviews', block.resourceType, block.resourceId]);
  }

  /** O review mais recente abre sozinho: é o que costuma estar por corrigir. */
  private expandFirst(): void {
    setTimeout(() => this.accordionItems?.first?.expand());
  }

  private toBlock(review: ReviewStatsFileReview): ReviewBlock {
    const findings = [...review.findings].sort((a, b) => this.rank(a) - this.rank(b));
    const verdict = VERDICTS[review.verdict ?? ''];
    // O mesmo fonte em dois changesets de uma issue daria dois itens com o mesmo nome.
    const changeset = review.resourceType === 'issue' && review.changesetNumber
      ? `cs ${review.changesetNumber}` : '';
    const label = [
      review.label || review.resourceId,
      changeset,
      resourceTypeLabel(review.resourceType) || review.resourceType,
      formatDateTime(review.requestedAt)
    ].filter(Boolean).join(' · ');
    const headline = this.headline(review, findings);
    let note = '';
    if (review.unavailable) {
      note = UNAVAILABLE_NOTES[review.unavailable] ?? 'O resultado deste review não pôde ser lido.';
    } else if (!findings.length) {
      note = 'Nenhum achado neste fonte.';
    }
    return {
      label,
      tag: headline.label,
      tagType: headline.type,
      verdict: verdict?.label ?? '',
      verdictType: verdict?.type ?? 'neutral',
      resourceType: review.resourceType,
      resourceId: review.resourceId,
      findings: findings.map(f => this.toCard(f)),
      note
    };
  }

  /** Resumo do item fechado: o mais grave que ele tem, ou por que não tem nada. */
  private headline(review: ReviewStatsFileReview, findings: ReviewStatsFileFinding[]): { label: string; type: TagType } {
    if (review.unavailable) { return { label: 'Indisponível', type: 'neutral' }; }
    if (!findings.length) { return { label: 'Sem achados', type: 'success' }; }
    const worst = findings[0];
    const count = findings.length === 1 ? '1 Achado' : `${findings.length} Achados`;
    if (worst.kind === 'required_action') {
      const severity = normalizeSeverity(worst.severity);
      const same = findings.filter(f => f.kind === 'required_action' && normalizeSeverity(f.severity) === severity).length;
      return { label: `${same} ${SEVERITY_LABELS[severity] ?? severity} · ${count}`, type: severityColor(severity) };
    }
    return { label: count, type: 'neutral' };
  }

  private toCard(f: ReviewStatsFileFinding): FindingCard {
    const isAction = f.kind === 'required_action';
    const severity = normalizeSeverity(f.severity);
    return {
      tag: isAction ? SEVERITY_LABELS[severity] ?? (f.severity || 'Sem severidade') : reviewCategoryLabel(f.category ?? '') || 'Sem categoria',
      tagType: isAction ? severityColor(severity) : 'neutral',
      kind: isAction ? 'Ação obrigatória' : 'Sugestão',
      line: f.line > 0 ? `Linha ${f.line}` : 'Sem linha',
      description: f.description,
      action: f.action ?? '',
      code: (f.code ?? '').trim(),
      filterKey: isAction ? (severity in SEVERITY_ORDER ? severity as FindingFilter : 'all') : 'suggestion'
    };
  }

  /** Totais do fonte no topo: quantas de cada severidade, e as sugestões. */
  private summarize(reviews: ReviewStatsFileReview[]): SummaryTag[] {
    const counts: Record<string, number> = { ALTA: 0, MEDIA: 0, BAIXA: 0 };
    let suggestions = 0;
    for (const review of reviews) {
      for (const f of review.findings) {
        if (f.kind === 'required_action') {
          const severity = normalizeSeverity(f.severity);
          if (severity in counts) { counts[severity]++; }
        } else {
          suggestions++;
        }
      }
    }
    const tags: SummaryTag[] = (['ALTA', 'MEDIA', 'BAIXA'] as const)
      .filter(key => counts[key] > 0)
      .map(key => ({
        label: `${counts[key]} ${SEVERITY_LABELS[key]}`, type: severityColor(key), filterKey: key, count: counts[key]
      }));
    if (suggestions) {
      tags.push({
        label: suggestions === 1 ? '1 Sugestão' : `${suggestions} Sugestões`, type: 'neutral',
        filterKey: 'suggestion', count: suggestions
      });
    }
    return tags;
  }

  /** "Todos" mais um botão por recorte que existe no fonte; sem dois recortes, não há o que filtrar. */
  private buildFilterButtons(): PoButtonGroupItem[] {
    if (this.summary.length < 2) { return []; }
    const labels: Record<string, string> = {
      ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa', suggestion: 'Sugestões'
    };
    const total = this.summary.reduce((sum, tag) => sum + tag.count, 0);
    const options: { key: FindingFilter; label: string }[] = [
      { key: 'all', label: `Todos (${total})` },
      ...this.summary.map(tag => ({ key: tag.filterKey, label: `${labels[tag.filterKey]} (${tag.count})` }))
    ];
    return options.map((option, index) => ({
      label: option.label,
      selected: index === 0,
      action: () => this.setFilter(option.key)
    }));
  }

  private setFilter(filter: FindingFilter): void {
    this.filter = filter;
  }

  /** Ações da mais grave para a menos grave, sugestões depois, na ordem do answer. */
  private rank(f: { kind: string; severity?: string }): number {
    if (f.kind !== 'required_action') { return 10; }
    return SEVERITY_ORDER[normalizeSeverity(f.severity)] ?? 3;
  }
}

/** RFC 3339 em UTC → data e hora locais, `dd/mm/aaaa hh:mm`. */
function formatDateTime(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) { return raw || ''; }
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}
